// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Code 128 barcodes, for module SN labels (component design §12): a module label is a
 * strip on a sub-assembly, where a linear barcode fits and any handheld scanner reads it.
 * Robot SNs keep their QR code.
 *
 * Drawn as an SVG path, as the QR code is — nothing fetched, sharp when printed. Code set
 * B carries the letters; a run of digits switches to code set C, two digits a symbol, so
 * a module SN (`150W2610230001`: four characters, then ten digits) is ten symbols rather
 * than fourteen, and its bars are wider on the same label.
 */

/** Bar and space widths of each symbol value, bar first; 106 is the stop pattern. */
const PATTERNS = [
  "212222",
  "222122",
  "222221",
  "121223",
  "121322",
  "131222",
  "122213",
  "122312",
  "132212",
  "221213",
  "221312",
  "231212",
  "112232",
  "122132",
  "122231",
  "113222",
  "123122",
  "123221",
  "223211",
  "221132",
  "221231",
  "213212",
  "223112",
  "312131",
  "311222",
  "321122",
  "321221",
  "312212",
  "322112",
  "322211",
  "212123",
  "212321",
  "232121",
  "111323",
  "131123",
  "131321",
  "112313",
  "132113",
  "132311",
  "211313",
  "231113",
  "231311",
  "112133",
  "112331",
  "132131",
  "113123",
  "113321",
  "133121",
  "313121",
  "211331",
  "231131",
  "213113",
  "213311",
  "213131",
  "311123",
  "311321",
  "331121",
  "312113",
  "312311",
  "332111",
  "314111",
  "221411",
  "431111",
  "111224",
  "111422",
  "121124",
  "121421",
  "141122",
  "141221",
  "112214",
  "112412",
  "122114",
  "122411",
  "142112",
  "142211",
  "241211",
  "221114",
  "413111",
  "241112",
  "134111",
  "111242",
  "121142",
  "121241",
  "114212",
  "124112",
  "124211",
  "411212",
  "421112",
  "421211",
  "212141",
  "214121",
  "412121",
  "111143",
  "111341",
  "131141",
  "114113",
  "114311",
  "411113",
  "411311",
  "113141",
  "114131",
  "311141",
  "411131",
  "211412",
  "211214",
  "211232",
  "2331112",
] as const;

const CODE_C = 99;
const CODE_B = 100;
const START_B = 104;
const START_C = 105;
const STOP = 106;

/** Quiet zone either side, in modules — ten is the minimum a scanner is specified for. */
const QUIET = 10;

function digitRun(text: string, from: number): number {
  let end = from;
  while (end < text.length) {
    const code = text.charCodeAt(end);
    if (code < 48 || code > 57) break;
    end++;
  }
  return end - from;
}

/** The symbol values for `text`, start and check symbol included, stop excluded. */
export function code128Values(text: string): number[] {
  for (const char of text) {
    const code = char.charCodeAt(0);
    if (code < 32 || code > 126)
      throw new Error(`Code 128 B cannot encode ${JSON.stringify(char)}`);
  }
  // Set C pays off for four digits at either end, six in the middle — fewer would cost
  // more in switching than it saves.
  const worthC = (from: number) => {
    const run = digitRun(text, from);
    const atEdge = from === 0 || from + run === text.length;
    return run >= (atEdge ? 4 : 6);
  };
  const values: number[] = [];
  let inC = text.length > 0 && worthC(0) && digitRun(text, 0) % 2 === 0;
  values.push(inC ? START_C : START_B);
  let index = 0;
  while (index < text.length) {
    if (inC) {
      if (digitRun(text, index) >= 2) {
        values.push(Number(text.slice(index, index + 2)));
        index += 2;
        continue;
      }
      values.push(CODE_B);
      inC = false;
    }
    // An odd run goes into C one digit late, so it ends on a whole pair.
    if (worthC(index) && digitRun(text, index) % 2 === 0) {
      values.push(CODE_C);
      inC = true;
      continue;
    }
    values.push(text.charCodeAt(index) - 32);
    index++;
  }
  const check = values.reduce((sum, value, position) => sum + value * Math.max(position, 1), 0);
  values.push(check % 103);
  return values;
}

/** The bars as one SVG path one unit tall, offset by the quiet zone; `width` in modules. */
export function code128Path(text: string): { path: string; width: number } {
  const widths = [...code128Values(text), STOP].flatMap((value) =>
    [...(PATTERNS[value] ?? "")].map(Number),
  );
  let x = QUIET;
  let path = "";
  widths.forEach((width, position) => {
    // Even positions are bars, odd ones spaces.
    if (position % 2 === 0) path += `M${x} 0h${width}v1h-${width}z`;
    x += width;
  });
  return { path, width: x + QUIET };
}
